import {
    useMutation,
    type UseMutationResult,
    useQuery,
    useQueryClient,
    type UseQueryResult,
} from "@tanstack/react-query";
import { Query } from "src/types/query";
import axios from "src/utils/axios";

export enum OrganizationPreferenceKeyEnum {
    PRICE_DISPLAY_MODE = "price-display-mode",
}

export type PricesDisplay = "exvat" | "inclvat";

export type OrganizationPreferenceValueMap = {
    [OrganizationPreferenceKeyEnum.PRICE_DISPLAY_MODE]: { vat: PricesDisplay };
};

export type OrganizationPreference<
    T extends OrganizationPreferenceKeyEnum = OrganizationPreferenceKeyEnum,
> = {
    id?: string;
    key: T;
    value: OrganizationPreferenceValueMap[T];
};

export function useQueryOrganizationPreferences(
    orgId: number
): UseQueryResult<OrganizationPreference<OrganizationPreferenceKeyEnum>[], unknown> {
    return useQuery({
        queryKey: [Query.OrganizationPreferences, orgId],
        queryFn: async () => (await axios.get(`organization/${orgId}/preferences`)).data,
    });
}

export function useSaveOrganizationPreferences(
    orgId: number
): UseMutationResult<void, unknown, OrganizationPreference<OrganizationPreferenceKeyEnum>[]> {
    const client = useQueryClient();
    return useMutation({
        onSuccess: () => {
            client.refetchQueries({
                queryKey: [Query.OrganizationPreferences, orgId],
            });
        },
        mutationFn: async (preferences: OrganizationPreference<OrganizationPreferenceKeyEnum>[]) =>
            await axios.post(`organization/${orgId}/preferences`, { preferences }),
    });
}

export function useUpdateOrganizationPreferences(
    orgId: number
): UseMutationResult<void, unknown, OrganizationPreference<OrganizationPreferenceKeyEnum>[]> {
    const client = useQueryClient();

    return useMutation({
        onSuccess: () => {
            client.refetchQueries({
                queryKey: [Query.OrganizationPreferences, orgId],
            });
        },
        mutationFn: async (preferences: OrganizationPreference<OrganizationPreferenceKeyEnum>[]) =>
            await axios.patch(`organization/${orgId}/preferences`, { preferences }),
    });
}
